import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
    MessageCircle, 
    Send, 
    Facebook, 
    Instagram, 
    RefreshCw, 
    Settings,
    User,
    Check,
    CheckCheck,
    AlertCircle,
    Inbox,
    Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SocialInbox() {
    const { user, token } = useAuth();
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null);
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [activeTab, setActiveTab] = useState('all');
    const [unreadCounts, setUnreadCounts] = useState({ facebook: 0, instagram: 0, total: 0 });
    const [searchQuery, setSearchQuery] = useState('');
    const messagesEndRef = useRef(null);

    useEffect(() => {
        fetchStatus();
        fetchConversations();
        fetchUnreadCounts();
        
        // Poll for new messages every 30 seconds
        const interval = setInterval(() => {
            fetchConversations();
            fetchUnreadCounts();
        }, 30000);
        
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const fetchStatus = async () => {
        try {
            const response = await fetch(`${API_URL}/api/social/status`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setStatus(data);
        } catch (error) {
            console.error('Failed to fetch status:', error);
        }
    };

    const fetchConversations = async () => {
        try {
            const platform = activeTab === 'all' ? '' : activeTab;
            const url = platform 
                ? `${API_URL}/api/social/conversations?platform=${platform}`
                : `${API_URL}/api/social/conversations`;
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setConversations(data);
            setLoading(false);
        } catch (error) {
            console.error('Failed to fetch conversations:', error);
            setLoading(false);
        }
    };

    const fetchUnreadCounts = async () => {
        try {
            const response = await fetch(`${API_URL}/api/social/unread-count`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setUnreadCounts(data);
        } catch (error) {
            console.error('Failed to fetch unread counts:', error);
        }
    };

    const fetchMessages = async (conversationId) => {
        try {
            const response = await fetch(
                `${API_URL}/api/social/conversations/${conversationId}/messages`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const data = await response.json();
            setMessages(data);
            
            // Update unread counts
            fetchUnreadCounts();
            fetchConversations();
        } catch (error) {
            console.error('Failed to fetch messages:', error);
        }
    };

    const selectConversation = (conversation) => {
        setSelectedConversation(conversation);
        fetchMessages(conversation.id);
    };

    const sendMessage = async () => {
        if (!newMessage.trim() || !selectedConversation) return;

        setSending(true);
        try {
            const response = await fetch(`${API_URL}/api/social/send`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    platform: selectedConversation.platform,
                    recipient_id: selectedConversation.participant_id,
                    message_text: newMessage
                })
            });

            if (response.ok) {
                setNewMessage('');
                fetchMessages(selectedConversation.id);
                toast.success('Message envoyé');
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Erreur lors de l\'envoi');
            }
        } catch (error) {
            toast.error('Erreur réseau');
        } finally {
            setSending(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const getPlatformIcon = (platform) => {
        switch (platform) {
            case 'facebook':
                return <Facebook className="h-4 w-4 text-blue-600" />;
            case 'instagram':
                return <Instagram className="h-4 w-4 text-pink-600" />;
            default:
                return <MessageCircle className="h-4 w-4" />;
        }
    };

    const getPlatformColor = (platform) => {
        switch (platform) {
            case 'facebook':
                return 'bg-blue-100 text-blue-800';
            case 'instagram':
                return 'bg-gradient-to-r from-purple-100 to-pink-100 text-pink-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diff = now - date;
        
        if (diff < 60000) return 'À l\'instant';
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
        if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
        return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
    };

    const filteredConversations = conversations.filter(conv => 
        conv.participant_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        conv.last_message.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Show setup instructions if not configured
    if (status && !status.meta_configured) {
        return (
            <Layout>
                <div className="max-w-4xl mx-auto p-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Settings className="h-6 w-6" />
                                Configuration requise
                            </CardTitle>
                            <CardDescription>
                                L'intégration Meta (Facebook/Instagram) n'est pas encore configurée.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                <h3 className="font-semibold text-amber-800 mb-2">
                                    Étapes de configuration :
                                </h3>
                                <ol className="list-decimal list-inside space-y-2 text-amber-700">
                                    <li>Créez une App sur <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="underline">developers.facebook.com</a></li>
                                    <li>Configurez les produits : Messenger et Instagram Basic Display</li>
                                    <li>Ajoutez votre Page Facebook et compte Instagram Business</li>
                                    <li>Récupérez les tokens d'accès</li>
                                    <li>Configurez le webhook avec l'URL: <code className="bg-amber-100 px-1 rounded">{API_URL}/api/webhook/meta</code></li>
                                </ol>
                            </div>
                            
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className={`p-4 rounded-lg border ${status?.facebook_configured ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <Facebook className="h-5 w-5 text-blue-600" />
                                        <span className="font-medium">Facebook Messenger</span>
                                        {status?.facebook_configured ? (
                                            <Badge className="bg-green-500">Connecté</Badge>
                                        ) : (
                                            <Badge variant="secondary">Non configuré</Badge>
                                        )}
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        Répondez aux messages de votre Page Facebook
                                    </p>
                                </div>
                                
                                <div className={`p-4 rounded-lg border ${status?.instagram_configured ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <Instagram className="h-5 w-5 text-pink-600" />
                                        <span className="font-medium">Instagram DM</span>
                                        {status?.instagram_configured ? (
                                            <Badge className="bg-green-500">Connecté</Badge>
                                        ) : (
                                            <Badge variant="secondary">Non configuré</Badge>
                                        )}
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        Répondez aux messages Instagram Direct
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="h-[calc(100vh-120px)] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Inbox className="h-6 w-6" />
                            Inbox Social
                        </h1>
                        <p className="text-muted-foreground">
                            Gérez vos messages Facebook et Instagram
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {unreadCounts.total > 0 && (
                            <Badge variant="destructive" className="text-sm">
                                {unreadCounts.total} non lu{unreadCounts.total > 1 ? 's' : ''}
                            </Badge>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                fetchConversations();
                                fetchUnreadCounts();
                            }}
                        >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Actualiser
                        </Button>
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 grid grid-cols-12 gap-4 min-h-0">
                    {/* Conversations List */}
                    <div className="col-span-4 flex flex-col border rounded-lg bg-card">
                        {/* Tabs */}
                        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSelectedConversation(null); }} className="p-2">
                            <TabsList className="w-full">
                                <TabsTrigger value="all" className="flex-1">
                                    Tous
                                    {unreadCounts.total > 0 && (
                                        <span className="ml-1 text-xs bg-destructive text-white rounded-full px-1.5">
                                            {unreadCounts.total}
                                        </span>
                                    )}
                                </TabsTrigger>
                                <TabsTrigger value="facebook" className="flex-1">
                                    <Facebook className="h-3 w-3 mr-1" />
                                    {unreadCounts.facebook > 0 && (
                                        <span className="ml-1 text-xs bg-blue-500 text-white rounded-full px-1.5">
                                            {unreadCounts.facebook}
                                        </span>
                                    )}
                                </TabsTrigger>
                                <TabsTrigger value="instagram" className="flex-1">
                                    <Instagram className="h-3 w-3 mr-1" />
                                    {unreadCounts.instagram > 0 && (
                                        <span className="ml-1 text-xs bg-pink-500 text-white rounded-full px-1.5">
                                            {unreadCounts.instagram}
                                        </span>
                                    )}
                                </TabsTrigger>
                            </TabsList>
                        </Tabs>
                        
                        {/* Search */}
                        <div className="px-2 pb-2">
                            <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Rechercher..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-8 h-8"
                                />
                            </div>
                        </div>

                        {/* Conversation List */}
                        <ScrollArea className="flex-1">
                            {loading ? (
                                <div className="p-4 text-center text-muted-foreground">
                                    Chargement...
                                </div>
                            ) : filteredConversations.length === 0 ? (
                                <div className="p-4 text-center text-muted-foreground">
                                    <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                    <p>Aucune conversation</p>
                                </div>
                            ) : (
                                <div className="divide-y">
                                    {filteredConversations.map((conv) => (
                                        <button
                                            key={conv.id}
                                            onClick={() => selectConversation(conv)}
                                            className={`w-full p-3 text-left hover:bg-muted/50 transition-colors ${
                                                selectedConversation?.id === conv.id ? 'bg-muted' : ''
                                            }`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className="relative">
                                                    {conv.participant_avatar ? (
                                                        <img
                                                            src={conv.participant_avatar}
                                                            alt=""
                                                            className="w-10 h-10 rounded-full"
                                                        />
                                                    ) : (
                                                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                                                            <User className="h-5 w-5 text-muted-foreground" />
                                                        </div>
                                                    )}
                                                    <div className="absolute -bottom-1 -right-1">
                                                        {getPlatformIcon(conv.platform)}
                                                    </div>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between">
                                                        <span className={`font-medium truncate ${conv.unread_count > 0 ? 'text-foreground' : 'text-muted-foreground'}`}>
                                                            {conv.participant_name}
                                                        </span>
                                                        <span className="text-xs text-muted-foreground">
                                                            {formatTime(conv.last_message_at)}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        {conv.last_message_by === 'business' && (
                                                            <CheckCheck className="h-3 w-3 text-blue-500 flex-shrink-0" />
                                                        )}
                                                        <p className={`text-sm truncate ${conv.unread_count > 0 ? 'font-medium' : 'text-muted-foreground'}`}>
                                                            {conv.last_message}
                                                        </p>
                                                    </div>
                                                </div>
                                                {conv.unread_count > 0 && (
                                                    <Badge variant="destructive" className="text-xs">
                                                        {conv.unread_count}
                                                    </Badge>
                                                )}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </ScrollArea>
                    </div>

                    {/* Messages Panel */}
                    <div className="col-span-8 flex flex-col border rounded-lg bg-card">
                        {selectedConversation ? (
                            <>
                                {/* Conversation Header */}
                                <div className="p-4 border-b flex items-center gap-3">
                                    {selectedConversation.participant_avatar ? (
                                        <img
                                            src={selectedConversation.participant_avatar}
                                            alt=""
                                            className="w-10 h-10 rounded-full"
                                        />
                                    ) : (
                                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                                            <User className="h-5 w-5 text-muted-foreground" />
                                        </div>
                                    )}
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold">
                                                {selectedConversation.participant_name}
                                            </span>
                                            <Badge className={getPlatformColor(selectedConversation.platform)}>
                                                {getPlatformIcon(selectedConversation.platform)}
                                                <span className="ml-1 capitalize">{selectedConversation.platform}</span>
                                            </Badge>
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            ID: {selectedConversation.participant_id}
                                        </p>
                                    </div>
                                </div>

                                {/* Messages */}
                                <ScrollArea className="flex-1 p-4">
                                    <div className="space-y-4">
                                        {messages.map((msg) => (
                                            <div
                                                key={msg.id}
                                                className={`flex ${msg.is_from_business ? 'justify-end' : 'justify-start'}`}
                                            >
                                                <div
                                                    className={`max-w-[70%] rounded-lg p-3 ${
                                                        msg.is_from_business
                                                            ? 'bg-primary text-primary-foreground'
                                                            : 'bg-muted'
                                                    }`}
                                                >
                                                    <p className="whitespace-pre-wrap">{msg.message_text}</p>
                                                    <div className={`flex items-center gap-1 mt-1 text-xs ${
                                                        msg.is_from_business ? 'text-primary-foreground/70' : 'text-muted-foreground'
                                                    }`}>
                                                        <span>{formatTime(msg.created_at)}</span>
                                                        {msg.is_from_business && msg.delivered && (
                                                            <CheckCheck className="h-3 w-3" />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        <div ref={messagesEndRef} />
                                    </div>
                                </ScrollArea>

                                {/* Message Input */}
                                <div className="p-4 border-t">
                                    <div className="flex gap-2">
                                        <Textarea
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Écrivez votre message..."
                                            className="min-h-[60px] resize-none"
                                            disabled={sending}
                                        />
                                        <Button
                                            onClick={sendMessage}
                                            disabled={!newMessage.trim() || sending}
                                            className="self-end"
                                        >
                                            <Send className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="flex-1 flex items-center justify-center text-muted-foreground">
                                <div className="text-center">
                                    <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                    <p className="text-lg font-medium">Sélectionnez une conversation</p>
                                    <p className="text-sm">Choisissez une conversation pour voir les messages</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
}
